import React, { FC, useMemo } from 'react';
import { Box, useTheme, Divider } from '@mui/material';
import {
  Typography,
  TagFilterV2Component,
  pluralize,
  DishSummary,
} from '@fdha/web-ui-library';
import { SectionType } from '@fdha/common-utils';
import {
  MealOptionType,
  MenuOptionSelectionAvailable,
} from '@fdha/graphql-api-patient';
import { useTranslation } from 'react-i18next';
import { TagFilterOption } from '@fdha/common-hooks';

interface MealkitVariant {
  id: string;
  ingredientName: string;
  quantity: number;
}

interface ChooseMealsMenuSectionProps {
  title: string;
  sectionType: SectionType;
  items: MenuOptionSelectionAvailable[];
  selectedCount: number;
  requiredCount: number;
  filterState: {
    filterOptions: TagFilterOption[];
    appliedFilters: string[];
    draftResults: MenuOptionSelectionAvailable[];
    results: MenuOptionSelectionAvailable[];
    handleApply: (selectedValues: string[]) => void;
    handleCancel: () => void;
    handleDraftChange: (selectedValues: string[]) => void;
  };
  onItemClick: (
    item: MenuOptionSelectionAvailable,
    sectionType: SectionType
  ) => void;
}

export const ChooseMealsMenuSection: FC<ChooseMealsMenuSectionProps> = ({
  title,
  sectionType,
  selectedCount,
  requiredCount,
  filterState,
  onItemClick,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const subSectionSubTitle = {
    prepared: 'prepared',
    mealKit: 'mealKit',
  };

  const getTags = (item: MenuOptionSelectionAvailable) => {
    const tags = item.tagsByCategory?.map(({ tags }) =>
      tags.map(({ name }) => name)
    );
    return tags?.flat();
  };

  // Extract filter state from props
  const {
    filterOptions,
    appliedFilters,
    draftResults,
    results: filteredItems,
    handleApply,
    handleCancel,
    handleDraftChange,
  } = filterState;

  const filteredPreparedMeals = filteredItems.filter(
    (item) => item.mealOptionType === MealOptionType.Dish
  );

  const filteredMealKits = filteredItems.filter(
    (item) => item.mealOptionType === MealOptionType.MealKit
  );

  // Filter labels
  const filterButtonConfirmLabel = useMemo(() => {
    const resultCount = draftResults.length;
    return `Show ${resultCount > 0 ? resultCount : 'All'} ${pluralize(
      resultCount,
      'Meal',
      undefined,
      true
    )}`;
  }, [draftResults.length]);

  const handleItemClick = (item: MenuOptionSelectionAvailable) => {
    onItemClick(item, sectionType);
  };

  const getSelectedVariants = (mealkitVariants: MealkitVariant[]) => {
    if (mealkitVariants.length === 1) return [];

    return mealkitVariants
      ?.filter((variant) => variant.quantity > 0)
      .map((variant) => ({
        name: variant.ingredientName,
        quantity: variant.quantity + 'x',
      }));
  };

  const renderFilter = () => {
    if (!filterOptions.length) return null;

    return (
      <Box sx={{ mb: 2 }}>
        <TagFilterV2Component
          key={`filter-${sectionType}`}
          anchorTitle={t('meals:chooseMeals.filters.title')}
          containerTitle={t('meals:chooseMeals.filters.title')}
          options={filterOptions}
          appliedValue={appliedFilters}
          confirmButtonLabel={filterButtonConfirmLabel}
          clearButtonLabel={t('meals:chooseMeals.filters.clearAll')}
          onDraftChange={handleDraftChange}
          onApply={handleApply}
          onCancel={handleCancel}
          results={filteredItems}
          v2={true}
        />
      </Box>
    );
  };

  const renderItems = (
    itemList: MenuOptionSelectionAvailable[],
    mealTypeKey: string
  ) => {
    if (itemList.length === 0) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Box padding={2} bgcolor={theme.palette.info.lightBg}>
          <>
            <Typography
              variant="h6"
              fontWeight="bold"
              i18nKey={`meals:chooseMeals.subheader.${mealTypeKey}`}
            >
              {t(`meals:chooseMeals.subheader.${mealTypeKey}`)}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              i18nKey={`meals:chooseMeals.subheader.${mealTypeKey}Description`}
            >
              {t(`meals:chooseMeals.subheader.${mealTypeKey}Description`)}
            </Typography>
          </>
        </Box>
        <Box display="flex" flexDirection="column" padding={2}>
          {itemList.map((item, index) => (
            <Box key={`${item.menuOptionId}`}>
              <DishSummary
                image={item?.photoUrl || ''}
                title={item.name}
                tags={getTags(item)}
                quantity={item.quantity || 0}
                onViewDetails={() => handleItemClick(item)}
                selectedVariants={getSelectedVariants(
                  item.mealkitVariants || []
                )}
                v2={true}
              />
              {index !== itemList.length - 1 && (
                <Divider sx={{ height: '1px', my: 2 }} />
              )}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Box display="flex" flexDirection="column" padding={2} rowGap={2}>
        <Box component="div">
          <Typography
            variant="h6"
            color="text.primary"
            i18nKey={`meals:chooseMeals.title.${sectionType}`}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            i18nKey={`meals:chooseMeals.selectionCount.${sectionType}`}
            i18nParams={{ selectedCount, requiredCount }}
          />
        </Box>
        {renderFilter()}
      </Box>

      {sectionType === 'entreeDishes' ? (
        <>
          {renderItems(filteredPreparedMeals, subSectionSubTitle.prepared)}
          {renderItems(filteredMealKits, subSectionSubTitle.mealKit)}
        </>
      ) : (
        renderItems(filteredItems, subSectionSubTitle.prepared)
      )}
    </Box>
  );
};
